import React, { useState } from 'react'
import { Button } from '@/components/Buttons/Button'
import { TextInput } from '@/components/Inputs'
import { useForm } from 'react-hook-form'
import { AuthErrorType, AuthOptionType } from '@/actors/AuthActor/types'
import { useTranslation } from '@/localization/useTranslation'
import { STANDARD_AUTH_FORMS, EMAIL_REGEX, NAME_MAX_LENGTH } from '@/constants/auth'
import { useAuth } from '@/hooks/auth/useAuth'
import { LoadingOverlay } from '@/components/Loading/Overlay'
import { AuthErrorModal } from '@/actors/AuthActor/components/AuthErrorModal'
import { FormHeader } from '@/actors/AuthActor/components/FormHeader'
import { IRegisterRequest } from '@/types/auth'
import TermsAndConditions from '@/actors/AuthActor/components/TermsAndContions'
import CustomField from './components/CustomField'
import type { TranslationType } from '@/types/common'

import { Stack } from '@/components/Stack'

type RegisterFormValues = {
  username: string
  password: string
  firstName: string
  lastName: string
  confirmEmail: string
  termsAndConditions: boolean
  [key: string]: unknown
}

interface RegisterProps {
  handleNavigateToForm: (path: string) => void
  form?: AuthOptionType['options']['register']
  termsAndConditions?: TranslationType
}

export const Register: React.FC<RegisterProps> = (props) => {
  const { handleNavigateToForm, form, termsAndConditions } = props
  const { registerMutation } = useAuth()
  const { isPending, error, reset } = registerMutation
  const { t } = useTranslation()

  const [termsAccepted, setTermsAccepted] = useState(false)
  const {
    register,
    control,
    getValues,
    handleSubmit,
    setValue,
    formState: { errors, isDirty, isSubmitting }
  } = useForm<RegisterFormValues>({
    defaultValues: {
      username: '',
      password: '',
      firstName: '',
      lastName: '',
      confirmEmail: '',
      termsAndConditions: false
    }
  })

  const onSubmit = async (data: RegisterFormValues) => {
    // Guards the Enter-key path the disabled button cannot reach. A second submit
    // re-runs register *and* its chained auto-login, minting two more tokens in
    // quick succession — the v3 score dip already documented in useAuth's
    // register handler, where a rejected auto-login leaves the account created
    // but not logged in.
    //
    // RHF's own `isSubmitting` rather than the mutation's `isPending`: it is set
    // before validation is awaited, so it covers the whole submit, and it resets
    // itself when the promise settles.
    if (isSubmitting) return

    try {
      await registerMutation.mutateAsync(data as unknown as IRegisterRequest)
    } catch (err) {
      console.error('Error during registration:', err)
    }
  }

  // A failed register deliberately demands a fresh terms acceptance — but the
  // button gate (`termsAccepted`) and the checkbox are separate state, so the
  // RHF field must be unchecked too. Otherwise dismissing the error modal
  // ("register with a different email") brings the form back with a checked
  // box and a disabled button that only a manual uncheck/recheck revives.
  const resetTermsAcceptance = () => {
    setTermsAccepted(false)
    setValue('termsAndConditions', false)
  }

  const handleError = () => {
    resetTermsAcceptance()
    reset()
  }

  const handleRedirect = (path: string) => {
    resetTermsAcceptance()
    reset()
    handleNavigateToForm(path)
  }

  return (
    <>
      <LoadingOverlay isLoading={isPending} />
      <AuthErrorModal
        error={error as unknown as AuthErrorType}
        handleError={handleError}
        handleRedirect={handleRedirect}
      />
      <FormHeader
        title={form?.title || 'Create account'}
        subtitle={form?.subtitle}
        message={<>*{t('Required fields')}</>}
      />
      <form action="#" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <TextInput
            errors={errors}
            label={t('Name')}
            required
            {...register('firstName', {
              setValueAs: (value: string) => value?.trim(),
              required: {
                value: true,
                message: t('Name is required')
              },
              maxLength: {
                value: NAME_MAX_LENGTH,
                message: t('Must be at most {maxLength} characters long', { maxLength: NAME_MAX_LENGTH })
              }
            })}
          />
          <TextInput
            errors={errors}
            label={t('Last name')}
            required
            {...register('lastName', {
              setValueAs: (value: string) => value?.trim(),
              required: {
                value: true,
                message: t('Last name is required')
              },
              maxLength: {
                value: NAME_MAX_LENGTH,
                message: t('Must be at most {maxLength} characters long', { maxLength: NAME_MAX_LENGTH })
              }
            })}
          />
          {form?.customFields?.map(field => (
            <CustomField
              key={field.name}
              field={field}
              control={control}
              errors={errors}
            />
          ))}
          <TextInput
            errors={errors}
            label={t('Email')}
            required
            {...register('username', {
              required: {
                value: true,
                message: t('Email is required')
              },
              pattern: {
                value: EMAIL_REGEX,
                message: t('Invalid email')
              }
            })}
          />
          <TextInput
            errors={errors}
            label={t('Confirm email')}
            canPasteField={false}
            required
            {...register('confirmEmail', {
              required: {
                value: true,
                message: t('Email is required')
              },
              pattern: {
                value: EMAIL_REGEX,
                message: t('Invalid email')
              },
              validate: (val: unknown) => {
                if (getValues('username') !== val) {
                  return t('Your emails do no match')
                }
              }
            })}
          />
          <TextInput
            errors={errors}
            label={t('Password')}
            type="password"
            required
            {...register('password', {
              required: {
                value: true,
                message: t('Password is required')
              },
              minLength: {
                value: 6,
                message: t('Password must be at least {minLength} characters long', { minLength: 6 })
              }
            })
            }
          />
        </div>

        <TermsAndConditions
          termsAndConditions={termsAndConditions}
          {...register('termsAndConditions', {
            required: true,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTermsAccepted(e.target.checked)
          })}
        />

        <Button
          variant="primary"
          type="submit"
          disabled={!isDirty || !termsAccepted || isSubmitting}
          fullWidth
          data-testid="auth-form-button-register-submit"
        >
          {t('Create account')}
        </Button>
        <Button
          onClick={() => handleNavigateToForm(STANDARD_AUTH_FORMS.MAIN)}
          variant="tertiary"
          fullWidth
          data-testid="auth-form-button-register-back"
        >
          {t('Back')}
        </Button>
      </form>
    </>
  )
}
